/**
 * cart-upsell.js
 * Handles swap and add actions for the cart upsell block.
 * Includes loading overlay and GA4 event tracking.
 *
 * GA4 Events:
 *   upsell_impression — upsell block present when drawer opens
 *   upsell_click      — user clicked the swap or add button
 *   upsell_complete   — cart was successfully updated
 *   upsell_error      — cart operation failed
 */

(function () {
  'use strict';

  // ─── GA4 Tracking ───────────────────────────────────────────────

  function trackEvent(eventName, params) {
    if (typeof gtag !== 'function') return;
    gtag('event', eventName, params);
  }

  function trackImpression(type) {
    trackEvent('upsell_impression', {
      upsell_type: type,
      item_name: 'Canvas Series Upsell'
    });
  }

  function trackClick(type) {
    trackEvent('upsell_click', {
      upsell_type: type,
      item_name: 'Canvas Series Upsell'
    });
  }

  function trackComplete(type) {
    trackEvent('upsell_complete', {
      upsell_type: type,
      item_name: 'Canvas Series Upsell'
    });
  }

  function trackError(type, errorMessage) {
    trackEvent('upsell_error', {
      upsell_type: type,
      item_name: 'Canvas Series Upsell',
      error_message: errorMessage
    });
  }

  // ─── Impression Tracking ─────────────────────────────────────────

  /**
   * Track impressions by checking for upsell blocks in the DOM
   * when the drawer opens. Uses a Set to ensure each block only
   * fires once per page load even if the drawer is closed and reopened.
   */
  const impressedBlocks = new Set();

  function fireImpressions() {
    document.querySelectorAll('.cart-upsell-block').forEach((block) => {
      if (impressedBlocks.has(block)) return;
      impressedBlocks.add(block);
      trackImpression(block.dataset.upsellType);
    });
  }

  // ─── Loading Overlay ────────────────────────────────────────────

  function showCartLoading() {
    const drawer = document.querySelector('.drawer__inner');
    if (!drawer) return;
    const overlay = document.createElement('div');
    overlay.id = 'cart-upsell-loading';
    overlay.innerHTML = `
      <div class="cart-upsell-loading__inner">
        <div class="cart-upsell-spinner"></div>
        <p class="cart-upsell-loading__text">Updating cart…</p>
      </div>
    `;
    drawer.appendChild(overlay);
  }

  function hideCartLoading() {
    const overlay = document.getElementById('cart-upsell-loading');
    if (overlay) overlay.remove();
  }

  // ─── Cart API ───────────────────────────────────────────────────

  async function addToCart(variantId, quantity = 1, sellingPlan = null) {
    const item = { id: parseInt(variantId, 10), quantity };
    if (sellingPlan) item.selling_plan = parseInt(sellingPlan, 10);
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [item] }),
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.description || 'Failed to add item to cart.');
    }
    return response.json();
  }

  async function removeFromCart(variantId) {
    const cartResponse = await fetch('/cart.js');
    const cart = await cartResponse.json();

    const variantIdInt = parseInt(variantId, 10);
    const lineIndex = cart.items.findIndex(
      (item) => item.variant_id === variantIdInt
    );

    if (lineIndex === -1) {
      throw new Error('Item not found in cart.');
    }

    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        line: lineIndex + 1,
        quantity: 0,
      }),
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.description || 'Failed to remove item from cart.');
    }
    return response.json();
  }

  // ─── Cart Drawer Refresh ─────────────────────────────────────────

  async function refreshCartDrawer() {
    const response = await fetch(
      `${window.location.pathname}?sections=cart-drawer,cart-icon-bubble`
    );
    if (!response.ok) return;

    const sections = await response.json();

    const drawerEl = document.getElementById('CartDrawer');
    if (drawerEl && sections['cart-drawer']) {
      const parsed = new DOMParser().parseFromString(
        sections['cart-drawer'],
        'text/html'
      );
      const newInner = parsed.querySelector('#CartDrawer');
      if (newInner) drawerEl.innerHTML = newInner.innerHTML;
    }

    const bubbleSection = document.getElementById('cart-icon-bubble');
    if (bubbleSection && sections['cart-icon-bubble']) {
      const parsed = new DOMParser().parseFromString(
        sections['cart-icon-bubble'],
        'text/html'
      );
      const newBubble = parsed.querySelector('.shopify-section');
      if (newBubble) bubbleSection.innerHTML = newBubble.innerHTML;
    }

    // Fire impressions for any new upsell blocks after refresh
    fireImpressions();
  }

  // ─── Click Handler ───────────────────────────────────────────────

  async function handleUpsellClick(button) {
    const action = button.dataset.upsellAction;
    const addVariant = button.dataset.addVariant;
    const removeVariant = button.dataset.removeVariant;
    const sellingPlan = button.dataset.sellingPlan || null;
    const upsellType = button.closest('.cart-upsell-block')?.dataset.upsellType || action;

    trackClick(upsellType);

    button.disabled = true;
    showCartLoading();

    try {
      if (action === 'swap') {
        await addToCart(addVariant, 1, sellingPlan);
        await removeFromCart(removeVariant);
      } else if (action === 'add') {
        await addToCart(addVariant, 1, sellingPlan);
      }

      trackComplete(upsellType);
      await refreshCartDrawer();

    } catch (error) {
      console.error('[CartUpsell] Error:', error);
      trackError(upsellType, error.message);
      hideCartLoading();
      button.disabled = false;
      button.textContent = 'Something went wrong — try again';
      return;
    }
  }

  // ─── Init ────────────────────────────────────────────────────────

  function init() {
    // Fire impressions for any upsell blocks already in the DOM
    fireImpressions();

    // Re-fire impressions when the cart icon is clicked to open the drawer
    // setTimeout gives the drawer animation time to complete
    document.addEventListener('click', function (event) {
      if (event.target.closest('#cart-icon-bubble')) {
        setTimeout(fireImpressions, 300);
      }
    });

    // Event delegation for upsell button clicks
    document.addEventListener('click', function (event) {
      const button = event.target.closest('[data-upsell-action]');
      if (!button) return;
      event.preventDefault();
      handleUpsellClick(button);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();